import { Box, Grid, Hidden, IconButton, makeStyles, SwipeableDrawer, Typography } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import React, { FC, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { prezzoAnnuoSelector, prezzoFinaleSelector, semestraleSelector } from "../../store/discount/discount.selectors";
import { prevStepsSelector, selectionsSelector, stepSelector } from "../../store/engine/engine.selectors";
import { Step } from "../../types/Step";
import { ReactComponent as IconaAuto } from "../../icons/IconaAuto.svg";
import { ReactComponent as IconaCasa } from "../../icons/IconaCasa.svg";
import { ReactComponent as IconaDecidiTu } from "../../icons/IconaDecidiTu.svg";
import { ReactComponent as IconaDipendente } from "../../icons/IconaDipendente.svg";
import { MassimaleModal } from "../modals/MassimaleModal";
import { Stepper } from "../Stepper";
import { BasicBox, BoxDivider, MultipleBox } from "./BasicBox";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: 1060,
    borderRadius: 10,
    backgroundColor: theme.palette.secondary.light,
    boxShadow: "0 4px 12px 0 rgba(13,12,12,0.02), 0 10px 30px 0 rgba(0,0,0,0.12)",
    padding: 42,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      height: "unset",
      minHeight: "100vh",
      width: 300,
      padding: 30,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
  paperSwipeableDrawer: {
    [theme.breakpoints.down("sm")]: {
      height: "100vh",
    },
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: "flex-end",
    position: "absolute",
    top: 0,
    right: 0,
  },
  spacer: {
    width: "100%",
    height: 73,
  },
  commonPanelRoot: {
    height: "100%",
  },
  iconButtonClose: {
    color: "white",
  },
  title: {
    marginBottom: 40,
    fontSize: 24,
    fontWeight: 700,
  },
  step: {
    marginBottom: 10,
    marginTop: 30,
  },
  box: {
    minHeight: 64,
    borderRadius: 5,
    backgroundColor: theme.palette.secondary.main,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    paddingLeft: 10,
    marginBottom: 9,
    position: "relative",
  },
  boxText: {
    display: "flex",
    flexDirection: "column",
  },
  boxTitle: {
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  boxDescription: {
    color: theme.palette.common.white,
  },
  boxInfoIcon: {
    color: "white",
    position: "absolute",
    width: 16,
    height: 16,
    top: 8,
    right: 10,
    cursor: "pointer",
  },
  boxIcon: {
    marginTop: 10,
    cursor: "pointer",
  },
  footerPrice: {
    color: theme.palette.primary.main,
  },
  footerDisclaimer: {
    fontSize: 12,
  },
  allungaPanelMd: {
    height: 400,
  },
}));

interface BoxProps {
  withBack: boolean;
  infoCallBack?: () => void;
  noBottomMargin?: boolean;
}

const BoxVivere: FC<BoxProps> = (props: BoxProps) => {
  return (
    <BasicBox
      icon={<IconaCasa />}
      step={Step.COSA}
      title="Vivere Pienamente - Vita Privata e Casa"
      description={<>Massimale 30.000 €</>}
      {...props}
    />
  );
};

const BoxMuoversi: FC<BoxProps> = (props: BoxProps) => {
  return (
    <BasicBox
      icon={<IconaAuto />}
      step={Step.COSA}
      title="Mobilità Platino CQC"
      description={
        <>
          Massimale 150.000 €<br />
          Massimale 5.000 € per Rimborso Autoscuola/Zero Punti
        </>
      }
      {...props}
    />
  );
};

const BoxDipendente: FC<BoxProps> = (props: BoxProps) => {
  return (
    <BasicBox
      icon={<IconaDipendente />}
      step={Step.COSA}
      title="Lavoro dipendente"
      description={<>Massimale 30.000 €</>}
      {...props}
    />
  );
};

const BoxDecidiTu: FC<BoxProps> = (props: BoxProps) => {
  return <BasicBox icon={<IconaDecidiTu />} step={Step.COSA} title="Decidi Tu!" {...props} />;
};

interface CheckProps {
  children: (withBack: boolean) => React.ReactElement;
  guard?: boolean;
  prevSteps: Step[];
  step: Step;
  currentStep: Step;
}

const Check: FC<CheckProps> = (props: CheckProps) => {
  const { children, guard, prevSteps, step, currentStep } = props;
  const isPreviousStep = prevSteps.indexOf(step) !== -1;
  return guard && (isPreviousStep || step === currentStep) ? children(isPreviousStep) : null;
};

/**
 * Panel commen part between desktop and mobile views
 */
const CommonPanel: FC = () => {
  const classes = useStyles();
  const [open, setOpen] = useState(false); // MassimaleDialog
  const closeMassimale = useCallback(() => setOpen(false), [setOpen]);
  const step = useSelector(stepSelector);
  const prezzo = useSelector(prezzoFinaleSelector);
  const prezzoAnnuo = useSelector(prezzoAnnuoSelector);
  const selections = useSelector(selectionsSelector);
  const prevSteps = useSelector(prevStepsSelector);
  const semestrale = useSelector(semestraleSelector);

  return (
    <>
      <Box className={classes.commonPanelRoot}>
        <Box>
          <Typography variant={"h3"} className={classes.title}>
            Il tuo preventivo
          </Typography>
        </Box>
        <Box>
          <Check
            guard={selections.muoversi && !selections.vivere && !selections.dipendente}
            step={Step.COSA}
            currentStep={step}
            prevSteps={prevSteps}
          >
            {(withBack: boolean) => <BoxMuoversi withBack={withBack} />}
          </Check>

          <Check
            guard={selections.muoversi && selections.vivere && !selections.dipendente}
            step={Step.COSA}
            currentStep={step}
            prevSteps={prevSteps}
          >
            {(withBack: boolean) => (
              <MultipleBox>
                <BoxMuoversi withBack={withBack} noBottomMargin />
                <BoxDivider />
                <BoxVivere withBack={withBack} noBottomMargin />
              </MultipleBox>
            )}
          </Check>

          <Check
            guard={selections.muoversi && selections.vivere && selections.dipendente}
            step={Step.COSA}
            currentStep={step}
            prevSteps={prevSteps}
          >
            {(withBack: boolean) => (
              <MultipleBox>
                <BoxMuoversi withBack={withBack} noBottomMargin />
                <BoxDivider />
                <BoxVivere withBack={withBack} noBottomMargin />
                <BoxDivider />
                <BoxDipendente withBack={withBack} noBottomMargin />
              </MultipleBox>
            )}
          </Check>
          {step === Step.SCONTO && (
            <Check guard={selections.deciditu} step={Step.SCONTO} currentStep={step} prevSteps={prevSteps}>
              {(withBack: boolean) => <BoxDecidiTu withBack={withBack} />}
            </Check>
          )}
        </Box>
      </Box>
      {/* Box attached to bottom due to "space-between" parent property */}
      <Hidden only={["xs", "sm"]}>
        <Box>
          <Typography variant={"h6"}>{step === Step.SCONTO ? "Prezzo finale*" : "Prezzo a partire da*"}</Typography>
          <Typography variant={"h1"} className={classes.footerPrice} data-test="prezzo-finale">
            {prezzo !== undefined && prezzo.toLocaleString("it-IT", { style: "currency", currency: "EUR" })}
          </Typography>
          <Typography variant={"subtitle2"} data-test="prezzo-annuale">
            {step === Step.SCONTO && prezzoAnnuo !== undefined && semestrale && (
              <>(Annuale {prezzoAnnuo.toLocaleString("it-IT", { style: "currency", currency: "EUR" })})</>
            )}
          </Typography>
          <Typography className={classes.footerDisclaimer}>
            {step === Step.SCONTO
              ? "* Se scelto il pagamento semestrale, conteggiati interessi del 4% con minimo di € 5"
              : "* Prezzo finale che potrà variare a seconda di quello che sceglierai da 80 € a 295 €"}
          </Typography>
        </Box>
      </Hidden>
      <MassimaleModal open={open} handleClose={closeMassimale} />
    </>
  );
};

export const GridPanel: FC = () => {
  const classes = useStyles();
  return (
    <Hidden smDown>
      <Grid item xs={4} style={{ position: "relative" }}>
        <Grid container>
          <Grid item xs={11} className={classes.root}>
            <CommonPanel />
          </Grid>
        </Grid>
        <Stepper />
      </Grid>
    </Hidden>
  );
};

interface SwipeablePanelProps {
  open: boolean;
  onClose: (event: React.MouseEvent<HTMLButtonElement | HTMLElement>) => void;
  onOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export const SwipeablePanel: FC<SwipeablePanelProps> = (props: SwipeablePanelProps) => {
  const classes = useStyles();
  return (
    <Hidden mdUp>
      <SwipeableDrawer
        anchor="right"
        open={props.open}
        onClose={props.onClose}
        onOpen={props.onOpen}
        elevation={0}
        variant="temporary"
        classes={{ paper: classes.paperSwipeableDrawer }}
      >
        <Grid container>
          <Grid item xs={12} className={classes.root}>
            <div className={classes.drawerHeader}>
              <IconButton onClick={props.onClose} className={classes.iconButtonClose}>
                <Close />
              </IconButton>
            </div>
            <CommonPanel />
          </Grid>
        </Grid>
      </SwipeableDrawer>
    </Hidden>
  );
};
